import {
    Component,
    Input,
    ViewChild, ChangeDetectorRef, DestroyRef, OnInit, ChangeDetectionStrategy, SimpleChanges, OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Paginator } from '../../../shared/interfaces/paginator';
import { Product } from '../../../shared/interfaces/product';
import { Sale } from '../../../shared/interfaces/sale';
import { Step } from '../../../shared/interfaces/step';
import { ProductStepService } from '../../services/product-step.service';
import { ProductCardComponent } from '../product-card/product-card.component';
import { Brand } from '../../../shared/interfaces/brand';
import { Category } from '../../../shared/interfaces/category';
import { Subcategory } from '../../../shared/interfaces/subcategory';
import { ProductFamily } from './interfaces/product-family';
import { ProductFamiliesUtil } from './utils/product-families-util';
import { Cart } from '../../classes/cart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Workflow } from '../../../shared/interfaces/workflow';
import { TagCount } from '../../../shared/interfaces/tag';
import { DisplayPenProductsComponent } from '../display-pen-products/display-pen-products.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-step-display-products',
    standalone: true,
    imports: [
        CommonModule,
        ProductCardComponent,
        MatPaginatorModule,
        MatRadioModule,
        ReactiveFormsModule,
        DisplayPenProductsComponent,
    ],
    templateUrl: './step-display-products.component.html',
    styleUrl: './step-display-products.component.scss',
})
export class StepDisplayProductsComponent implements OnChanges, OnInit {
    public brands$: Observable<Brand[]>;

    public categories$: Observable<Category[]>;

    public families$: Observable<ProductFamily[]>;

    @Input({ required: true }) public formGroup!: FormGroup;

    public loading = false;

    @ViewChild(MatPaginator) public paginator: MatPaginator | undefined;

    public products$: Observable<Paginator<Product> | undefined>;

    @Input({ required: true }) public sale!: Sale;

    public readonly searchControl = new FormControl('', { nonNullable: true });

    public selectedBrand: string = '';

    public selectedCategory: string = '';

    public selectedSubcategory: string = '';

    public selectedTag = 0;

    @Input({ required: true }) public step!: Step;

    public subcategories$: Observable<Subcategory[]>;

    public tags$: Observable<TagCount[] | undefined>;

    public type?: string | null;

    @Input({ required: true }) public workflow!: Workflow;

    private readonly _brands = new BehaviorSubject<Brand[]>([]);

    private readonly _categories = new BehaviorSubject<Category[]>([]);

    private readonly _families = new BehaviorSubject<ProductFamily[]>([]);

    private readonly _products = new BehaviorSubject<Paginator<Product> | undefined>(void 0);

    private readonly _subcategories = new BehaviorSubject<Subcategory[]>([]);

    private readonly _tags = new BehaviorSubject<TagCount[] | undefined>([]);

    public constructor(
        public readonly cart: Cart,
        private readonly products: ProductStepService,
        private readonly cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
    ) {
        this.products$ = this._products.asObservable();
        this.categories$ = this._categories.asObservable();
        this.subcategories$ = this._subcategories.asObservable();
        this.brands$ = this._brands.asObservable();
        this.families$ = this._families.asObservable();
        this.tags$ = this._tags.asObservable();
    }

    public clearSearch(): void {
        this.searchControl.setValue('');
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['sale']) {
            this.loadProducts();
        }
    }

    public ngOnInit(): void {
        this.cart.changed.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => this.cdr.markForCheck());

        this.searchControl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => {
            if (this.paginator) {
                this.paginator.pageIndex = 0;
            }
            this.loadProducts();
        });
    }

    public onBrandChange(value: string): void {
        this.selectedBrand = value;
        this.paginator!.pageIndex = 0;
        this.loadProducts();
    }

    public onCategoryChange(value: string): void {
        this.selectedCategory = value;
        this.selectedSubcategory = '';
        this.selectedBrand = '';
        this.selectedTag = 0;
        this.paginator!.pageIndex = 0;
        this.loadProducts();
    }

    public onPage(): void {
        this.loadProducts();
    }

    public onSubcategoryChange(value: string): void {
        this.selectedSubcategory = value;
        this.selectedBrand = '';
        this.paginator!.pageIndex = 0;
        this.loadProducts();
    }

    public onTagChange(value: number): void {
        this.selectedTag = value;
        this.selectedCategory = '';
        this.selectedSubcategory = '';
        this.selectedBrand = '';
        this.paginator!.pageIndex = 0;
        this.loadProducts();
    }

    private forcePaginate(products: Product[]): Paginator<Product> {
        return {
            current_page: 1,
            per_page: products.length,
            total: products.length,
            data: products,
        };
    }

    private loadProducts(): void {
        let params: { [key: string]: string | number } | undefined;
        if (this.paginator) {
            params = {
                page: this.paginator.pageIndex + 1,
                per_page: this.paginator.pageSize,
                category: this.selectedCategory || '',
                subcategory: this.selectedSubcategory || '',
                brand: this.selectedBrand || '',
                tag_id: this.selectedTag || 0,
                search: this.searchControl.value.trim(),
            };
        }

        this.loading = true;
        this.cdr.markForCheck();
        this.products.forStep(
            this.step.workflow_id,
            this.step.id,
            this.sale.id,
            params,
        ).subscribe((res) => {
            this.loading = false;
            this.type  = res.product_type;
            const data = res.data;
            let products = data.products;

            // if not paginated, paginate them anyway for cleaner code
            if (Array.isArray(products)) {
                products = this.forcePaginate(products);
            }

            this._products.next(products);
            this._families.next(ProductFamiliesUtil.groupByFamily(products.data));
            this._categories.next(data.categories);
            this._subcategories.next(data.subcategories ?? []);
            this._brands.next(data.brands ?? []);
            this._tags.next(data.tags);
            this.cdr.markForCheck();
        });
    }
}
